import { createTheme, ColorSystemOptions } from '@mui/material';
import scssVariables from '@styles/variables.module.scss';

import { timeLogThemeOptions } from './timelogDefault.theme';

// Light mode palette configuration
export const paletteLight: ColorSystemOptions = {
  palette: {
    background: {
      default: scssVariables.ColorBlack10,
      paper: scssVariables.ColorBlack10,
      body: scssVariables.ColorBlack20,
    },
    text: {
      primary: scssVariables.ColorBlack100,
      secondary: scssVariables.ColorBlack80,
      disabled: scssVariables.ColorBlack60,
    },
    primary: {
      main: scssVariables.ColorPink80Hex,
      light: scssVariables.ColorPink40Hex,
      dark: scssVariables.ColorPink100Hex,
    },
    secondary: {
      main: scssVariables.ColorFog80Hex,
      light: scssVariables.ColorFog40Hex,
      dark: scssVariables.ColorFog100Hex,
    },
    divider: scssVariables.ColorBlack40,
  },
};

// Dark mode palette configuration
export const paletteDark: ColorSystemOptions = {
  palette: {
    background: {
      default: scssVariables.ColorBlack80,
      paper: scssVariables.ColorBlack100,
      body: scssVariables.ColorBlack100,
    },
    text: {
      primary: scssVariables.ColorBlack10,
      secondary: scssVariables.ColorBlack20,
      disabled: scssVariables.ColorBlack40,
    },
    primary: {
      main: scssVariables.ColorPink40Hex,
      light: scssVariables.ColorPink20Hex,
      dark: scssVariables.ColorPink60Hex,
    },
    secondary: {
      main: scssVariables.ColorFog40Hex,
      light: scssVariables.ColorFog20Hex,
      dark: scssVariables.ColorFog60Hex,
    },
    divider: scssVariables.ColorBlack60,
  },
};

/**
 * Creates a Timelog theme with light and dark color schemes
 * @param locales - Optional locale configurations for MUI components
 * @returns Material-UI theme with color schemes configured
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createTimelogTheme = (...locales: any[]) => {
  return createTheme(
    {
      ...timeLogThemeOptions,
      palette: {
        ...timeLogThemeOptions.palette,
        ...paletteLight.palette,
      },
      colorSchemes: {
        light: paletteLight,
        dark: paletteDark,
      },
      cssVariables: {
        colorSchemeSelector: 'data',
      },
    },
    ...locales,
  );
};

/**
 * Creates a theme for use inside embeddable web components (Shadow DOM).
 * Uses cssVariables with ':host' selector so the variable declarations are scoped
 * to the shadow host element and cascade into the shadow DOM.
 * @param locales - Optional locale configurations for MUI components
 * @returns Material-UI theme with CSS variables scoped to :host
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createEmbeddableTimelogTheme = (...locales: any[]) => {
  return createTheme(
    {
      ...timeLogThemeOptions,
      palette: {
        ...timeLogThemeOptions.palette,
        ...paletteLight.palette,
      },
      colorSchemes: {
        light: paletteLight,
        // for simplicity just do not pass dark palette along for now
        // dark: paletteDark,
      },
      defaultColorScheme: 'light',
      cssVariables: {
        rootSelector: ':host',
      },
    },
    ...locales,
  );
};

/**
 * Creates Storybook-specific themes that force a single color scheme
 * @param mode - 'light' or 'dark'
 * @returns Theme configuration for Storybook
 */
export const createStorybookTheme = (mode: 'light' | 'dark') => {
  const palette = mode === 'light' ? paletteLight : paletteDark;

  return createTheme({
    ...timeLogThemeOptions,
    palette: {
      ...timeLogThemeOptions.palette,
      ...palette.palette,
    },
    colorSchemes: {
      [mode]: palette,
    },
    defaultColorScheme: mode,
    cssVariables: {
      colorSchemeSelector: 'data',
    },
  });
};
